import {
  getActiveSession,
  principalKeyOf,
  useSessionStore,
} from "@auth/sessionStore";
import { datadogRum } from "@datadog/browser-rum";
import { reactPlugin } from "@datadog/browser-rum-react";
import { RUNNING_BUILD } from "@version/deployedBuild";

import { getConfig } from "../config";

/** Redacted by name whatever the value. Routes are static, so record ids ride the
 * query string; `q` and trace `search` carry typed free text. A `cursor` is
 * base64 JSON that can embed both, out of reach of the uuid check. */
const REDACTED_QUERY_PARAMS = new Set([
  "visit",
  "patient",
  "anchor",
  "record",
  "note",
  "q",
  "search",
  "cursor",
]);

const REDACTED = "REDACTED";

const UUID = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";

/** Separate instances: `test()` on a `g` regex advances `lastIndex`, skipping every other value. */
const UUID_TEST = new RegExp(UUID, "i");
const UUID_REPLACE_ALL = new RegExp(UUID, "gi");

/** `scrubUrl`'s by-name rules for unparseable text; a value ends at `&`, whitespace, or a LoAF `TAG[src=…]`'s `]`. */
const TEXT_PARAM_REPLACE_ALL = new RegExp(
  `\\b(${[...REDACTED_QUERY_PARAMS].join("|")}|x-amz-[a-z0-9-]*)=[^&\\s\\]]*`,
  "gi",
);

/** Lets repeat `initRum()` calls replace the subscriber rather than stack one. */
let unsubscribePrincipal: (() => void) | undefined;

/** Strips identifiers from a URL but keeps its shape, so views still group by page. */
function scrubUrl(rawUrl: string): string {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    // Unparseable: drop it rather than pass through something unexamined.
    return REDACTED;
  }

  for (const [key, value] of Array.from(url.searchParams)) {
    // A uuid value under any key covers params added later; `x-amz-*` is presigned S3 credential material.
    const lowerKey = key.toLowerCase();
    if (
      REDACTED_QUERY_PARAMS.has(lowerKey) ||
      lowerKey.startsWith("x-amz-") ||
      UUID_TEST.test(value)
    ) {
      url.searchParams.set(key, REDACTED);
    }
  }

  // API resource paths are uuid-keyed (`/visits/<uuid>/tasks`).
  url.pathname = url.pathname.replace(UUID_REPLACE_ALL, REDACTED);

  return url.toString();
}

/** `scrubUrl`'s rules by substring, for error messages, stacks and LoAF invokers. */
function scrubText(text: string): string {
  return text
    .replace(UUID_REPLACE_ALL, REDACTED)
    .replace(TEXT_PARAM_REPLACE_ALL, `$1=${REDACTED}`);
}

/** Ids only: never `name` or `email` (PHI-adjacent). `id` is the principal key
 * because a clinic-scoped session has no user. */
function syncPrincipal(): void {
  const session = getActiveSession();

  if (!session) {
    datadogRum.clearUser();
    return;
  }

  datadogRum.setUser({
    id: principalKeyOf(session),
    userId: session.userUuid,
    clinicId: session.clinicUuid,
    scope: session.scope,
  });
}

/** No-ops unless config enables RUM. */
export function initRum(): void {
  const { apiUrl, datadog } = getConfig();

  // Before the guard, so a call finding RUM disabled leaves no subscriber behind.
  unsubscribePrincipal?.();
  unsubscribePrincipal = undefined;

  if (!datadog.enabled || !datadog.applicationId || !datadog.clientToken) {
    return;
  }

  datadogRum.init({
    applicationId: datadog.applicationId,
    clientToken: datadog.clientToken,
    site: datadog.site,
    service: datadog.service,
    env: datadog.env,
    version: RUNNING_BUILD,

    sessionSampleRate: datadog.sessionSampleRate,
    sessionReplaySampleRate: datadog.sessionReplaySampleRate,

    // `mask` skips media `src`, so media elements carry `data-dd-privacy="hidden"`; nothing uses "allow".
    defaultPrivacyLevel: datadog.defaultPrivacyLevel,
    // Otherwise an action's name is the clicked text: pet and owner names.
    enablePrivacyForActionName: true,

    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,

    // `wsUrl` is absent: WebSockets can't carry tracing headers.
    allowedTracingUrls: [
      (url: string) => {
        try {
          return new URL(url).origin === new URL(apiUrl).origin;
        } catch {
          return false;
        }
      },
    ],

    beforeSend: (event) => {
      if (event.view.url) {
        event.view.url = scrubUrl(event.view.url);
      }
      if (event.view.referrer) {
        event.view.referrer = scrubUrl(event.view.referrer);
      }
      // Separate from the resource event; often a presigned clinic-computer screenshot.
      if (event.type === "view" && event.view.performance?.lcp?.resource_url) {
        event.view.performance.lcp.resource_url = scrubUrl(
          event.view.performance.lcp.resource_url,
        );
      }
      if (event.type === "resource" && event.resource.url) {
        event.resource.url = scrubUrl(event.resource.url);
      }
      if (event.type === "error") {
        // browser-rum 7.x never fills `error.resource` (kept defensively); request URLs ride in `message`/`stack`.
        if (event.error.resource?.url) {
          event.error.resource.url = scrubUrl(event.error.resource.url);
        }
        event.error.message = scrubText(event.error.message);
        if (event.error.stack) {
          event.error.stack = scrubText(event.error.stack);
        }
        if (event.error.handling_stack) {
          event.error.handling_stack = scrubText(event.error.handling_stack);
        }
      }
      if (event.type === "long_task" && event.long_task.scripts) {
        for (const script of event.long_task.scripts) {
          if (script.source_url) {
            script.source_url = scrubUrl(script.source_url);
          }
          // An id-less element's invoker embeds its `src`, e.g. `VIDEO[src=<presigned url>].onloadedmetadata`.
          if (script.invoker) {
            script.invoker = scrubText(script.invoker);
          }
        }
      }
      return true;
    },

    // Paired with AppShell's wrapped `createRouter`: without it, RUM silently records no views.
    plugins: [reactPlugin({ router: true })],
  });

  // Sessions rehydrate during import, so the first view isn't anonymous.
  syncPrincipal();
  unsubscribePrincipal = useSessionStore.subscribe(syncPrincipal);
}
